import React, { useState } from "react";
import { useLocation } from "wouter";
import { SafeLink as Link } from "@/components/safe-link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BusinessSwitcher } from "@/components/ui/business-switcher";
import { useAuth } from "@/providers/auth-provider";
import { useBusinessContext } from "@/providers/business-provider";
import { useLoading } from "@/providers/loading-provider";
import { ProfileAvatar } from "@/components/ui/profile-avatar";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import logoImage from "@assets/ZentroBooksLogo.png";
import { 
  LayoutDashboard, 
  ReceiptText, 
  BarChart3, 
  CheckSquare, 
  MessageSquare, 
  Settings, 
  Menu, 
  X, 
  LogOut,
  BookOpen,
  ChevronRight,
  FileText,
  CreditCard,
  ShoppingBag,
  ClipboardList,
  Users,
  Store,
  Receipt,
  DollarSign,
  FileCheck,
  Building,
  Building2,
  TrendingUp,
  UserCheck,
  Wallet,
  FileSpreadsheet,
  BookText,
  Scale,
  Shield,
  Brain,
  Bell,
  Search,
  Sparkles,
  Megaphone,
  User,
  Zap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  hidden?: boolean;
}

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const { user, logout } = useAuth();
  const { activeBusiness } = useBusinessContext();
  const { navigateTo } = useLoading();

  const isAdmin = user?.isAdmin || user?.email === "hello@zentrobooks.com";

  // Group items into categories
  const subscriptionItems: NavItem[] = [
    {
      title: "Subscription",
      href: "/dashboard/subscription",
      icon: <CreditCard className="h-5 w-5" />,
    },
  ];

  const transactionItems: NavItem[] = [
    {
      title: "All Transactions",
      href: "/transactions",
      icon: <ReceiptText className="h-5 w-5" />,
    },
    {
      title: "Bank Register",
      href: "/transaction-register",
      icon: <BookText className="h-5 w-5" />,
    },
    {
      title: "Chart of Accounts",
      href: "/direct-accounts",
      icon: <BookOpen className="h-5 w-5" />,
    },
    {
      title: "Journal Entries",
      href: "/journal-entries",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      title: "Receipts",
      href: "/bookkeeping/receipts",
      icon: <Receipt className="h-5 w-5" />,
    },
    {
      title: "Automation Rules",
      href: "/bookkeeping/rules",
      icon: <Zap className="h-5 w-5" />,
    },
    {
      title: "Bank Reconciliation",
      href: "/bank-reconciliation",
      icon: <Scale className="h-5 w-5" />,
    },
  ];
  
  const salesItems: NavItem[] = [
    {
      title: "Customers",
      href: "/sales/customers",
      icon: <Users className="h-5 w-5" />,
    },
    {
      title: "Estimates",
      href: "/sales/estimates",
      icon: <ClipboardList className="h-5 w-5" />,
    },
    {
      title: "Invoices",
      href: "/sales/invoices",
      icon: <Receipt className="h-5 w-5" />,
    },
    {
      title: "Products & Services",
      href: "/sales/products",
      icon: <Store className="h-5 w-5" />,
    },
  ];
  
  const expensesItems: NavItem[] = [
    {
      title: "1099 Filings",
      href: "/expenses/tax-filings",
      icon: <FileSpreadsheet className="h-5 w-5" />,
    },
    {
      title: "Bill Payments",
      href: "/expenses/bill-payments",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      title: "Bills",
      href: "/expenses/bills",
      icon: <FileCheck className="h-5 w-5" />,
    },
    {
      title: "Contractors",
      href: "/expenses/contractors",
      icon: <UserCheck className="h-5 w-5" />,
    },
    {
      title: "Expenses",
      href: "/expenses/expenses",
      icon: <Wallet className="h-5 w-5" />,
    },
    {
      title: "Vendors",
      href: "/expenses/vendors",
      icon: <Building className="h-5 w-5" />,
    },
  ];
  
  const inventoryItems: NavItem[] = [
    {
      title: "Inventory Dashboard",
      href: "/inventory/dashboard",
      icon: <BarChart3 className="h-5 w-5" />,
    },
    {
      title: "Inventory",
      href: "/inventory",
      icon: <Store className="h-5 w-5" />,
    },
  ];

  const zentroMindItems: NavItem[] = [
    {
      title: "AI Budget Forecasting",
      href: "/budgets",
      icon: <TrendingUp className="h-5 w-5" />,
    },
    {
      title: "AI Cash Flow Projections",
      href: "/cash-flow/projections",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      title: "AI Tax Reminders",
      href: "/tax-reminders",
      icon: <Bell className="h-5 w-5" />,
    },
    {
      title: "AI Tax Research",
      href: "/tax-research",
      icon: <Search className="h-5 w-5" />,
    },
  ];

  const documentsItems: NavItem[] = [
    {
      title: "Documents",
      href: "/documents",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      title: "Legal Documents",
      href: "/legal",
      icon: <BookText className="h-5 w-5" />,
    },
  ];

  const payrollItems: NavItem[] = [
    {
      title: "Payroll Integration",
      href: "/payroll",
      icon: <Users className="h-5 w-5" />,
      badge: "New",
    },
  ];
  
  const reportItems: NavItem[] = [
    {
      title: "Enhanced Reports",
      href: "/reports-dashboard",
      icon: <BarChart3 className="h-5 w-5" />,
      badge: "New",
    },
    {
      title: "Reports",
      href: "/reports",
      icon: <BarChart3 className="h-5 w-5" />,
    },
    {
      title: "Trial Balance",
      href: "/reports/trial-balance",
      icon: <Scale className="h-5 w-5" />,
    },
    {
      title: "General Ledger",
      href: "/reports/general-ledger",
      icon: <BookOpen className="h-5 w-5" />,
    },
  ];
  

  const utilityItems: NavItem[] = [
    {
      title: "Tasks",
      href: "/tasks",
      icon: <CheckSquare className="h-5 w-5" />,
    },
    {
      title: "Zentro AI Assistant",
      href: "/chat",
      icon: <Sparkles className="h-5 w-5" />,
    },
    {
      title: "AI Bookkeeper",
      href: "/ai-bookkeeper",
      icon: <Brain className="h-5 w-5" />,
    },
  ];
  
  const zentroAdminItems: NavItem[] = [
    {
      title: "Admin Dashboard",
      href: "/admin/dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
    },
    {
      title: "Compliance Monitoring",
      href: "/admin/compliance",
      icon: <Shield className="h-5 w-5" />,
    },
    {
      title: "Client Management",
      href: "/dashboard/client-management",
      icon: <Users className="h-5 w-5" />,
    },
    {
      title: "Subscription",
      href: "/dashboard/subscription",
      icon: <CreditCard className="h-5 w-5" />,
    },
    {
      title: "Subscription Plans",
      href: "/dashboard/subscription-plans",
      icon: <CreditCard className="h-5 w-5" />,
    },
    {
      title: "Advanced Reports (Admin)",
      href: "/reports/jasper",
      icon: <BarChart3 className="h-5 w-5" />,
    },
    {
      title: "COA Templates",
      href: "/admin/coa-templates",
      icon: <BookOpen className="h-5 w-5" />,
    },
    {
      title: "Announcements",
      href: "/zentro-admin/announcements",
      icon: <Megaphone className="h-5 w-5" />,
    },
    {
      title: "Settings",
      href: "/settings",
      icon: <Settings className="h-5 w-5" />,
    },
  ];
  

  
  // Combine all item groups (excluding salesItems, expensesItems, inventoryItems, reportItems, zentroAdminItems, and subscriptionItems since they're in expandable menus)
  const navItems: NavItem[] = [
    ...payrollItems,
    ...utilityItems,
  ];

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleMenu = (menuName: string) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  // Function to determine which menu should be open based on current location
  const getActiveMenu = () => {
    if (location.startsWith("/transactions") || location.startsWith("/transaction-register") || location.startsWith("/bank-register") || location.startsWith("/direct-accounts") || location.startsWith("/bank-reconciliation") || location.startsWith("/bookkeeping")) return "transactions";
    if (location.startsWith("/sales/")) return "sales";
    if (location.startsWith("/expenses/")) return "expenses";
    if (location.startsWith("/inventory")) return "inventory";
    if (location.startsWith("/reports/")) return "reports";
    if (location.startsWith("/documents/")) return "documents";
    if (location.startsWith("/budgets") || location.startsWith("/cash-flow/projections") || location.startsWith("/tax-reminders") || location.startsWith("/tax-research") || location.startsWith("/financial-health") || location.startsWith("/zentromind/")) return "zentroMind";
    if (location.startsWith("/admin/") || location.startsWith("/dashboard/client-management") || location.startsWith("/dashboard/subscription-plans") || location.startsWith("/settings")) return "zentroAdmin";
    return null;
  };

  // Auto-open the appropriate menu based on current location
  React.useEffect(() => {
    const activeMenu = getActiveMenu();
    if (activeMenu && openMenu !== activeMenu) {
      setOpenMenu(activeMenu);
    }
  }, [location]);

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden fixed top-3 right-3 z-50 bg-background/70 backdrop-blur-sm shadow-sm border"
        onClick={toggleMobileMenu}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Sidebar for mobile and desktop */}
      <aside
        className={cn(
          "bg-white text-black border-r border-gray-200 w-full md:w-64 flex-shrink-0 flex flex-col h-screen md:fixed md:top-0 md:left-0 md:bottom-0",
          mobileOpen ? "fixed inset-0 z-40 animate-in slide-in-from-left duration-300" : "hidden md:flex",
          className
        )}
      >
        {/* App logo and branding — click returns to welcome dashboard */}
        <div className="px-4 py-5 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center justify-center flex-1">
            <img
              src={logoImage}
              alt="ZentroBooks Logo"
              className="w-[180px] object-contain cursor-pointer hover:opacity-80 transition-opacity"
              style={{ maxHeight: '60px' }}
              onClick={async () => {
                try {
                  await fetch("/api/businesses/clear-selection", { method: "POST", credentials: "include" });
                } catch {}
                localStorage.removeItem("activeBusinessId");
                const uid = user?.id;
                if (uid) localStorage.removeItem(`activeBusinessId_user_${uid}`);
                window.location.href = "/dashboard";
              }}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={toggleMobileMenu}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Business switcher */}
        <BusinessSwitcher />

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-2 space-y-1">
            {/* Dashboard - First Item */}
            <div className="w-full">
              <Link href="/dashboard">
                <div
                  className={cn(
                    "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                    location === "/dashboard"
                      ? "text-black"
                      : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    navigateTo("/dashboard");
                  }}
                >
                  <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                    <LayoutDashboard className="h-5 w-5" />
                  </div>
                  <span className="ml-3 md:text-sm text-base">Dashboard</span>
                </div>
              </Link>
            </div>



            {/* Business-scoped menu items — hidden until a business is selected */}
            {!activeBusiness ? (
              <div className="px-3 py-6 text-center">
                <p className="text-xs text-gray-400">Select a business to get started</p>
              </div>
            ) : (<>

            {/* Business Profile */}
            <div className="w-full">
              <Link href="/business/profile">
                <div
                  className={cn(
                    "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                    location === "/business/profile"
                      ? "text-black"
                      : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                  )}
                  onClick={(e) => { e.preventDefault(); setMobileOpen(false); navigateTo("/business/profile"); }}
                >
                  <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <span className="ml-3 md:text-sm text-base">Business Profile</span>
                </div>
              </Link>
            </div>

            {/* Subscription & Billing */}
            <div className="w-full">
              <Link href="/settings/billing">
                <div
                  className={cn(
                    "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                    location.startsWith("/settings/billing") || location.startsWith("/settings/licenses") || location.startsWith("/subscription")
                      ? "text-black"
                      : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                  )}
                  onClick={(e) => { e.preventDefault(); setMobileOpen(false); navigateTo("/settings/billing"); }}
                >
                  <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                    <CreditCard className="h-5 w-5" />
                  </div>
                  <span className="ml-3 md:text-sm text-base">Subscription &amp; Billing</span>
                </div>
              </Link>
            </div>

            {/* Transactions Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location === "/transactions" || location === "/transaction-register")
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('transactions')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <ReceiptText className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">Transactions</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'transactions' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'transactions' && (
                <div className="ml-6 mt-1 space-y-1">
                  {transactionItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Sales Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location.startsWith("/sales/"))
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('sales')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <DollarSign className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">Sales</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'sales' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'sales' && (
                <div className="ml-6 mt-1 space-y-1">
                  {salesItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Expenses Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location.startsWith("/expenses/"))
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('expenses')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <Wallet className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">Expenses</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'expenses' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'expenses' && (
                <div className="ml-6 mt-1 space-y-1">
                  {expensesItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Inventory Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location.startsWith("/inventory/"))
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('inventory')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <ShoppingBag className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">Inventory</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'inventory' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'inventory' && (
                <div className="ml-6 mt-1 space-y-1">
                  {inventoryItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* ZentroMind Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location.startsWith("/zentromind/"))
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('zentroMind')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <Brain className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">ZentroMind</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'zentroMind' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'zentroMind' && (
                <div className="ml-6 mt-1 space-y-1">
                  {zentroMindItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Reports Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location.startsWith("/reports"))
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('reports')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <BarChart3 className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">Reports</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'reports' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'reports' && (
                <div className="ml-6 mt-1 space-y-1">
                  {reportItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                        {item.badge && (
                          <Badge variant="secondary" className="ml-auto text-xs px-2 py-0.5 !bg-[#359df3] !text-white">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Documents Expandable Menu */}
            <div className="w-full">
              <div
                className={cn(
                  "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                  (location.startsWith("/documents") || location.startsWith("/legal"))
                    ? "text-black"
                    : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                )}
                onClick={() => toggleMenu('documents')}
              >
                <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                  <FileText className="h-5 w-5" />
                </div>
                <span className="ml-3 md:text-sm text-base">Documents</span>
                <ChevronRight 
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                    openMenu === 'documents' ? "rotate-90" : ""
                  )}
                />
              </div>
              
              {/* Submenu that expands/collapses */}
              {openMenu === 'documents' && (
                <div className="ml-6 mt-1 space-y-1">
                  {documentsItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                      <div
                        className={cn(
                          "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                          location === item.href
                            ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                            : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMobileOpen(false);
                          navigateTo(item.href);
                        }}
                      >
                        <div className="flex items-center justify-center w-5 h-5 mr-3">
                          {item.icon}
                        </div>
                        <span>{item.title}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* ZentroAdmin Expandable Menu - Only visible to admin users */}
            {isAdmin && (
              <div className="w-full">
                <div
                  className={cn(
                    "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                    (location.startsWith("/admin") || location.startsWith("/dashboard/client-management") || location.startsWith("/dashboard/subscription-plans") || location.startsWith("/settings"))
                      ? "text-black"
                      : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                  )}
                  onClick={() => toggleMenu('zentroAdmin')}
                >
                  <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                    <Shield className="h-5 w-5" />
                  </div>
                  <span className="ml-3 md:text-sm text-base">ZentroAdmin</span>
                  <ChevronRight 
                    className={cn(
                      "ml-auto h-4 w-4 transition-transform duration-200 text-black",
                      openMenu === 'zentroAdmin' ? "rotate-90" : ""
                    )}
                  />
                </div>
                
                {/* Submenu that expands/collapses */}
                {openMenu === 'zentroAdmin' && (
                  <div className="ml-6 mt-1 space-y-1">
                    {zentroAdminItems.map((item) => (
                      <Link key={item.href} href={item.href}>
                        <div
                          className={cn(
                            "flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                            location === item.href
                              ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                              : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            setMobileOpen(false);
                            navigateTo(item.href);
                          }}
                        >
                          <div className="flex items-center justify-center w-5 h-5 mr-3">
                            {item.icon}
                          </div>
                          <span>{item.title}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Staff Management — visible to all tenant owners */}
            <div className="w-full">
              <Link href="/staff">
                <div
                  className={cn(
                    "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                    location === "/staff"
                      ? "bg-blue-50 text-[#359df3]"
                      : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    navigateTo("/staff");
                  }}
                >
                  <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                    <Users className="h-5 w-5" />
                  </div>
                  <span className="ml-3 md:text-sm text-base">Staff</span>
                </div>
              </Link>
            </div>

            {/* Regular Navigation Items */}
            {navItems
              .filter(item => !item.hidden) // Filter out hidden items
              .map((item) => (
                <div key={item.href} className="w-full">
                  <Link href={item.href}>
                    <div
                      className={cn(
                        "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                        item.title === "Zentro AI Assistant"
                          ? "!bg-orange-500 !text-white"
                          : location === item.href
                          ? "text-black"
                          : "text-black hover:bg-gray-50 hover:text-[#359df3]"
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setMobileOpen(false);
                        navigateTo(item.href);
                      }}
                    >
                      <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                        {item.icon}
                      </div>
                      <span className="ml-3 md:text-sm text-base">{item.title}</span>
                      {item.badge && (
                        <Badge variant="secondary" className="ml-auto text-xs px-2.5 py-1 md:py-0.5 !bg-[#359df3] !text-white">
                          {item.badge}
                        </Badge>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </>)}
          </div>
        </nav>

        {/* Profile Menu Item */}
        <div className="px-3 pb-2">
          <Link href="/profile">
            <div
              className={cn(
                "flex items-center px-3 py-2.5 md:py-2 text-sm font-medium rounded-md cursor-pointer transition-colors",
                location === "/profile"
                  ? "bg-blue-50 text-[#359df3] border-l-4 border-[#359df3]"
                  : "text-black hover:bg-gray-50 hover:text-[#359df3]"
              )}
              onClick={() => setMobileOpen(false)}
            >
              <div className="flex items-center justify-center w-7 h-7 md:w-5 md:h-5">
                <User className="h-5 w-5" />
              </div>
              <span className="ml-3 md:text-sm text-base">My Profile</span>
            </div>
          </Link>
        </div>

        {/* User profile + notifications */}
        <div className="border-t border-[hsl(var(--sidebar-border))] p-4 mt-auto">
          <div className="flex items-center justify-between mb-2">
            <NotificationCenter />
          </div>
          <div className="flex items-center">
            <ProfileAvatar 
              profilePicture={user?.profilePicture ?? undefined}
              fullName={user?.fullName ?? undefined}
              username={user?.username}
              size="md"
              className="h-9 w-9 md:h-8 md:w-8"
            />
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-base md:text-sm font-medium text-[hsl(var(--sidebar-foreground))] truncate">
                {user?.fullName || user?.username}
              </p>
              <p className="text-sm md:text-xs text-[hsl(var(--sidebar-foreground))]/70 truncate">
                {user?.email}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                await logout();
                setMobileOpen(false);
              }}
              className="text-[hsl(var(--sidebar-foreground))]/70 hover:text-[hsl(var(--sidebar-foreground))]"
            >
              <LogOut className="h-5 w-5 md:h-4 md:w-4" />
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}
